import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { isTauri } from '@tauri-apps/api/core';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-bootstrap-setup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bootstrap-setup.component.html',
  styleUrl: './bootstrap-setup.component.css'
})
export class BootstrapSetupComponent implements OnInit {
  isSettingUp = false;
  statusText = 'Checking Server Initialization...';
  progress = 0;
  totalStr = '';

  constructor(private cdr: ChangeDetectorRef, private route: ActivatedRoute, private router: Router) {}

  async ngOnInit() {
    if (!isTauri()) return;
    
    // Simulate checking if MySQL database is active via normal requests or IPC
    // For now we boot directly into setup if it triggers
    this.startInstallerUI();
  }

  async startInstallerUI() {
    this.isSettingUp = true;
    listen('download-progress', (event: any) => {
      const payload = event.payload;
      if (payload.total) {
         this.progress = Math.round((payload.downloaded / payload.total) * 100);
         this.totalStr = (payload.total / 1024 / 1024).toFixed(2) + ' MB';
      }
      
      let label = "Service Components";
      const url = payload.url.toLowerCase();
      if (url.includes('runtime')) label = "SHIVAM Runtime";
      else if (url.includes('onlineotas')) label = "SHIVAM API Service";
      else if (url.includes('cloud-printing')) label = "SHIVAM Print Service";
      else if (url.includes('engine')) label = "Database Engine";

      this.statusText = `Downloading ${label} - ${this.progress}%`;
      this.cdr.detectChanges();
    });
    await this.triggerDownload();
  }

  async fetchConfigProperties(): Promise<any> {
      try {
          const propText: string = await invoke('fetch_properties', { url: "https://drive.google.com/uc?export=download&id=11VZNrVjEZ_t9tawWOXA2gdyKibXZ8MtY" });
          const props: any = {};
          propText.split('\n').forEach(line => {
              const l = line.trim();
              if (l && !l.startsWith('#')) {
                  const parts = l.split('=');
                  if (parts.length >= 2) {
                      const key = parts[0].trim();
                      const value = parts.slice(1).join('=').trim().replace('http://', 'https://');
                      props[key] = value;
                  }
              }
          });
          return props;
      } catch (e) {
          console.error("Failed to fetch configurations from server:", e);
          throw new Error("Unable to reach update servers. Please check your internet connection.");
      }
  }

  async triggerDownload() {
    this.isSettingUp = true;
    this.statusText = 'Downloading Prerequisites...';
    try {
        const needsRuntime = this.route.snapshot.queryParams['needsRuntime'] === 'true';
        const needsEngine = this.route.snapshot.queryParams['needsEngine'] === 'true';

        this.statusText = 'Fetching Configuration Endpoints...';
        const props = await this.fetchConfigProperties();
        const rootPath: string = await invoke('ipc_request', { arg: 'rootPath' });
        
        let drivePrefix = "C:";
        if (rootPath && rootPath.length >= 2 && rootPath[1] === ':') {
            drivePrefix = rootPath.substring(0, 2);
        }
        // Re-verify dependency integrity natively before triggering downloads
        const deps: any = await invoke('check_dependencies');

        // The engine directory is resolved natively: <drive>\ProgramData\ShivamEngine,
        // or the pre-2026-07-27 <drive>\tools\ShivamEngine when that is where the
        // datadir already lives. Never guess here - extracting a second engine beside
        // a live datadir would orphan the database.
        const engineBasePath: string = deps.engine_base || `${drivePrefix}\\ProgramData\\ShivamEngine`;

        if (needsRuntime || !deps.runtime_valid) {
            const runtimeArchivePath = `${rootPath}\\resources\\runtime.7z`;
            this.statusText = 'Downloading SHIVAM Runtime...';
            await invoke('download_file_with_progress', { url: props['RUNTIME_64.URL'], destPath: runtimeArchivePath });
            this.statusText = 'Extracting SHIVAM Runtime...';
            await invoke('extract_7z', { archivePath: runtimeArchivePath, destPath: `${rootPath}\\resources` });
            
            // Cleanup archive after extraction
            try { await invoke('remove_file', { path: runtimeArchivePath }); } catch(e) {}

            this.statusText = 'Downloading Cloud Print Service...';
            await invoke('download_file_with_progress', { url: props['PRINT_64.URL'], destPath: `${rootPath}\\resources\\runtime\\bin\\cloud-printing.jar` });
        }

        if (deps.is_server) {
            const apiArchivePath = `${rootPath}\\resources\\runtime\\bin\\onlineotas-${props['VERSION']}-SNAPSHOT.7z`;
            await invoke('download_file_with_progress', { url: props['API_64.URL'], destPath: apiArchivePath });
            this.statusText = 'Extracting API Server...';
            await invoke('extract_7z', { archivePath: apiArchivePath, destPath: `${rootPath}\\resources\\runtime\\bin` });
            
            // Cleanup archive
            try { await invoke('remove_file', { path: apiArchivePath }); } catch(e) {}
        }

        if (needsEngine || (!deps.mysql_service_up && deps.is_server)) {
            const engineArchivePath = `${engineBasePath}\\engine.7z`;
            this.statusText = 'Downloading Database Engine...';
            await invoke('download_file_with_progress', { url: props['ENGINE_64.URL'], destPath: engineArchivePath });
            this.statusText = 'Extracting Database Engine...';
            await invoke('extract_7z', { archivePath: engineArchivePath, destPath: engineBasePath });
            
            // Cleanup engine archive
            try { await invoke('remove_file', { path: engineArchivePath }); } catch(e) {}

            // No db.sql/data.sql import any more (WS-DBM Phase 2): the schema is
            // created EMPTY below and Liquibase builds every table, view, routine
            // and seed row at the first STODA start. Importing the old dumps here
            // would resurrect the pre-Liquibase schema - retired views as tables,
            // no databasechangelog - which is exactly the state a stale install
            // produced on 2026-07-27.
            this.statusText = 'Downloading Service Utilities...';
            await invoke('download_file_with_progress', { url: `${props['BASE.URL']}/dependencies/nssm.exe`, destPath: `${rootPath}\\resources\\runtime\\bin\\stoda.exe` });
        }

        if (needsRuntime || needsEngine) {
            this.statusText = 'Configuring Database and Background Services... Requesting UAC...';

            // ----------------------------------------------------------------
            // STODA service launcher.
            //
            // nssm stores AppStdout/AppStderr IN THE SERVICE REGISTRATION, so a
            // file name computed while installing is frozen for the life of that
            // registration - which is why every install kept appending to
            // stdout_<install-date>.log for ever (confirmed live 2026-07-27).
            // nssm runs this launcher instead of java.exe so the name, and the
            // pruning of old logs, happen on every service START.
            //
            // Keep in step with New-StodaLauncher in
            // src-tauri/windows/provision.ps1 - that is the primary installer,
            // this is the Setup Wizard repair path.
            //
            // Written via base64 so that none of > | % ( ) needs batch escaping;
            // hand-escaping a pipeline into `echo` lines is exactly where these
            // generated scripts break.
            // ----------------------------------------------------------------
            const binDir = `${rootPath}\\resources\\runtime\\bin`;
            const launcherPath = `${binDir}\\start-stoda.cmd`;
            const retainDays = 10;

            // ----------------------------------------------------------------
            // Memory plan - the SAME formula as provision.ps1's Get-MemoryPlan
            // (keep them in step): MySQL buffer pool ~32% of RAM (1..24 G),
            // redo = pool/4 (1..4 G), one pool instance per pool GB (max 8),
            // JVM -Xmx = RAM/4 (1..12 G), -Xms = Xmx/2. RAM comes from the
            // Rust get_total_memory_bytes command (GlobalMemoryStatusEx) -
            // navigator.deviceMemory is browser-capped at 8 GB and would
            // mis-size every larger machine. 0/failure => 8 GB profile.
            // ----------------------------------------------------------------
            let totalBytes = 0;
            try { totalBytes = await invoke<number>('get_total_memory_bytes'); } catch (e) {}
            const totalGB = Math.max(1, Math.round((totalBytes > 0 ? totalBytes : 8 * 2 ** 30) / 2 ** 30));
            const poolGB = Math.max(1, Math.min(24, Math.floor(totalGB * 0.32)));
            const poolInstances = Math.max(1, Math.min(8, poolGB));
            const redoGB = Math.max(1, Math.min(4, Math.floor(poolGB / 4)));
            const xmxGB = Math.max(1, Math.min(12, Math.floor(totalGB / 4)));
            const xmsGB = Math.max(1, Math.floor(xmxGB / 2));
            const jvmArgs = `-Xms${xmsGB}g -Xmx${xmxGB}g -XX:+UseG1GC -XX:MaxGCPauseMillis=200 -XX:+UseStringDeduplication -XX:+HeapDumpOnOutOfMemoryError -XX:HeapDumpPath="%LOGDIR%" -XX:+ExitOnOutOfMemoryError`;

            // Tuned my.ini - mirror of provision.ps1's New-MySqlConfig; written
            // base64 like the launcher and ONLY when no my.ini exists (hand
            // tuning always wins). FORWARD slashes in the paths: the MySQL
            // option-file parser treats backslash as an escape (C:\tools ->
            // "C:<TAB>ools"), proven with mysqld --validate-config.
            const engineFwd = engineBasePath.replace(/\\/g, '/');
            const myIniLines = [
                '# ==========================================================================',
                '#  SHIVAM MySQL configuration - GENERATED by the SHIVAM Setup Wizard.',
                `#  Memory profile computed from this machine's RAM (${totalGB} GB).`,
                '#  An existing my.ini is never overwritten, so hand tuning survives;',
                '#  delete the file and re-run the wizard to regenerate it.',
                '# ==========================================================================',
                '[mysqld]',
                `basedir=${engineFwd}`,
                `datadir=${engineFwd}/Data`,
                'port=3306',
                '',
                '# --- memory (Spring Boot shares this machine - pool is ~32% of RAM) ---',
                `innodb_buffer_pool_size=${poolGB}G`,
                `innodb_buffer_pool_instances=${poolInstances}`,
                `innodb_redo_log_capacity=${redoGB}G`,
                'innodb_log_buffer_size=64M',
                'tmp_table_size=128M',
                'max_heap_table_size=128M',
                '',
                '# --- durability: financial data - NEVER relax these two ---',
                'innodb_flush_log_at_trx_commit=1',
                'sync_binlog=1',
                '',
                '# --- I/O: unbuffered is the Windows flush method (O_DIRECT is Linux-only);',
                '#     io_capacity pinned below the 8.4 NVMe-assuming default of 10000.',
                'innodb_flush_method=unbuffered',
                'innodb_io_capacity=2000',
                'innodb_io_capacity_max=4000',
                '',
                '# --- connections / caches (thread_cache_size autosizes since 8.0) ---',
                'max_connections=200',
                'table_open_cache=4000',
                'max_allowed_packet=64M',
                'group_concat_max_len=131072',
                '',
                '# --- binary log: kept for point-in-time recovery, disk bounded to 3 days ---',
                'binlog_expire_logs_seconds=259200',
                'max_binlog_size=256M',
                '',
                '# --- charset: must match the Liquibase-built schema ---',
                'character-set-server=utf8mb4',
                'collation-server=utf8mb4_0900_ai_ci',
                '',
                '# X-protocol plugin is unused by the application (frees port 33060).',
                'mysqlx=OFF',
                '',
                "# skip-name-resolve is DELIBERATELY not set: with name resolution off a TCP",
                "# connection from 127.0.0.1 no longer matches the 'root'@'localhost' grant",
                '# and the API would be locked out of its own database.'
            ];
            const myIniB64 = btoa(myIniLines.join('\r\n') + '\r\n');

            // SECURITY-SETTINGS.txt - the user-facing antivirus/firewall
            // whitelisting guide (owner-authored per-vendor steps, 2026-07-28),
            // personalized with this install's folders/programs/ports. Keep the
            // text in step with Write-SecurityGuide in provision.ps1.
            const guideLines = [
                '===========================================================================',
                ' SHIVAM - SECURITY SETTINGS (antivirus and firewall whitelisting guide)',
                ' Generated by the SHIVAM Setup Wizard. Do not edit - regenerated on repair.',
                '===========================================================================',
                '',
                'To ensure this application runs smoothly, it is necessary to include the',
                'application folder in the exclusion list of your antivirus software.',
                '',
                'WHAT TO WHITELIST ON THIS MACHINE',
                '',
                `  Folders  : ${rootPath}`,
                `             ${engineBasePath}              (server machines only)`,
                `  Programs : ${binDir}\\java.exe`,
                `             ${engineBasePath}\\bin\\mysqld.exe              (server machines only)`,
                `             ${binDir}\\stoda.exe`,
                '  Network  : allow inbound TCP 8434 (API) and TCP 3306 (MySQL) on server',
                '             machines. The installer creates the Windows Firewall rules',
                '             automatically ("SHIVAM API (TCP 8434)", "SHIVAM MySQL (TCP 3306)"',
                '             + program rules); if your antivirus suite ships its OWN firewall',
                '             replacing Windows Firewall (Kaspersky, Bitdefender, Quick Heal',
                '             and K7 suites often do), allow the same ports and programs there.',
                '',
                'Here is how to exclude this folder for various antivirus programs:',
                '',
                'Windows Defender',
                '(the installer adds these exclusions automatically when Defender is the',
                'active antivirus - the steps below are only needed if that failed, see',
                'install.log)',
                '1. Open Windows Security.',
                '2. Select Virus & threat protection.',
                '3. Click Manage settings under Virus & threat protection settings.',
                '4. Scroll down to Exclusions and select Add or remove exclusions.',
                '5. Click Add an exclusion, choose Folder, and select the application folder.',
                '',
                'Kaspersky',
                '1. Open Kaspersky and click Settings (the gear icon).',
                '2. Select Threats and Exclusions.',
                '3. Under the Exclusions section, click Manage exclusions.',
                '4. Use the Add button to select the application folder.',
                '',
                'Quick Heal',
                '1. Open the Quick Heal dashboard.',
                '2. Click on the Settings menu.',
                '3. Select Scan Settings -> Exclude Files and folders.',
                '4. Add the application folder to the list.',
                '',
                'Bitdefender',
                '1. Open Bitdefender and go to the Protection section.',
                '2. Select Antivirus -> Settings -> Exclusions.',
                '3. Open the List of exclusions and add the application folder.',
                '',
                'Avira',
                '1. Open Avira and go to Security.',
                '2. Click on Protection options and then Real-Time Protection.',
                '3. Select Exceptions and add the application folder.',
                '',
                'Total AV',
                '1. Open Total AV.',
                '2. Go to Settings.',
                '3. Select File Exclusions and add the application folder path.',
                '',
                'K7 Computing',
                '1. Open K7.',
                '2. Go to Tools and select External Settings.',
                '3. Choose Virus Exclusion and add the application folder.',
                '',
                'After adding the exclusions, no application file will be quarantined and',
                'the database will not be slowed down by real-time scanning.'
            ];
            const guideB64 = btoa(guideLines.join('\r\n') + '\r\n');
            const launcherLines = [
                '@echo off',
                'setlocal enableextensions',
                '',
                'rem GENERATED by the SHIVAM Setup Wizard - do not edit by hand.',
                'rem The name rolls when the SERVICE starts: an open redirection',
                'rem handle cannot be repointed while the process holds it.',
                '',
                'set "BINDIR=%~dp0"',
                'set "LOGDIR=%BINDIR%logs"',
                'if not exist "%LOGDIR%" mkdir "%LOGDIR%" 2>nul',
                '',
                'rem NEVER use the cmd built-in %DATE% for a file name: it is',
                'rem locale formatted and on most locales contains "/".',
                'set "TODAY="',
                `for /f "delims=" %%i in ('powershell -NoProfile -NonInteractive -Command "Get-Date -Format yyyy-MM-dd"') do set "TODAY=%%i"`,
                'if not defined TODAY set "TODAY=unknown"',
                '',
                'set "OUTLOG=%LOGDIR%\\stdout_%TODAY%.log"',
                'set "ERRLOG=%LOGDIR%\\stderr_%TODAY%.log"',
                '',
                `rem Retention: only this launcher's own logs are considered, so`,
                'rem print.log and operator files in here survive. The dateless',
                'rem stdout.log/stderr.log are electron-era leftovers packed inside',
                'rem the published runtime.7z (0 KB, mtime 2023) - prune them too.',
                `powershell -NoProfile -NonInteractive -Command "Get-ChildItem -LiteralPath '%LOGDIR%' -Filter '*.log' -File -ErrorAction SilentlyContinue | Where-Object { ($_.Name -like 'stdout_*' -or $_.Name -like 'stderr_*' -or $_.Name -like 'service*' -or $_.Name -eq 'stdout.log' -or $_.Name -eq 'stderr.log') -and $_.LastWriteTime -lt (Get-Date).AddDays(-${retainDays}) } | Remove-Item -Force -ErrorAction SilentlyContinue" >nul 2>&1`,
                '',
                '>>"%OUTLOG%" echo === STODA starting %TODAY% %TIME% ===',
                '',
                'rem JVM flags computed from this machine RAM (see the memory plan above);',
                'rem heap dumps land in the log dir, OOM exits so nssm restarts cleanly.',
                `"${binDir}\\java.exe" ${jvmArgs} -jar "${binDir}\\onlineotas-${props['VERSION']}-SNAPSHOT.jar" >>"%OUTLOG%" 2>>"%ERRLOG%"`,
                'set "RC=%ERRORLEVEL%"',
                '',
                '>>"%OUTLOG%" echo === STODA exited with code %RC% on %TODAY% at %TIME% ===',
                'exit /b %RC%'
            ];
            const launcherB64 = btoa(launcherLines.join('\r\n') + '\r\n');

            const scriptBody = `
@echo off
setlocal

if exist "${engineBasePath}\\bin\\mysqld.exe" (
    rem Tuned my.ini written only when absent - hand tuning always wins. The
    rem service is pinned to it via --defaults-file (keep in step with
    rem Install-MySqlService in provision.ps1).
    if not exist "${engineBasePath}\\my.ini" powershell -NoProfile -NonInteractive -Command "[IO.File]::WriteAllBytes('${engineBasePath}\\my.ini', [Convert]::FromBase64String('${myIniB64}'))"
    "${engineBasePath}\\bin\\mysqld.exe" --defaults-file="${engineBasePath}\\my.ini" --initialize-insecure
    "${engineBasePath}\\bin\\mysqld.exe" --install MySQL --defaults-file="${engineBasePath}\\my.ini"
    sc start MySQL
    timeout /t 5 /nobreak
    "${engineBasePath}\\bin\\mysql.exe" -u root -e "ALTER USER 'root'@'localhost' IDENTIFIED BY 'SasTha20*42Temp';"
    "${engineBasePath}\\bin\\mysql.exe" --user=root --password=SasTha20*42Temp -e "CREATE DATABASE IF NOT EXISTS \`online_otas_new\` CHARACTER SET utf8mb4 COLLATE utf8mb4_0900_ai_ci;"
)

rem Windows Firewall: LAN counters reach this machine on 8434 (API) and 3306
rem (MySQL). Idempotent delete-then-add; profile=any because counter LANs are
rem routinely classified Public. Keep the rule names in step with
rem Get-FirewallRuleSpecs in provision.ps1.
netsh advfirewall firewall delete rule name="SHIVAM API (TCP 8434)" >nul 2>&1
netsh advfirewall firewall add rule name="SHIVAM API (TCP 8434)" dir=in action=allow protocol=TCP localport=8434 profile=any
netsh advfirewall firewall delete rule name="SHIVAM MySQL (TCP 3306)" >nul 2>&1
netsh advfirewall firewall add rule name="SHIVAM MySQL (TCP 3306)" dir=in action=allow protocol=TCP localport=3306 profile=any
netsh advfirewall firewall delete rule name="SHIVAM API Server (java.exe)" >nul 2>&1
netsh advfirewall firewall add rule name="SHIVAM API Server (java.exe)" dir=in action=allow program="${binDir}\\java.exe" profile=any
netsh advfirewall firewall delete rule name="SHIVAM Database Engine (mysqld.exe)" >nul 2>&1
netsh advfirewall firewall add rule name="SHIVAM Database Engine (mysqld.exe)" dir=in action=allow program="${engineBasePath}\\bin\\mysqld.exe" profile=any

rem SECURITY-SETTINGS.txt: the per-antivirus whitelisting guide for the
rem operator (step-by-step for Defender/Kaspersky/Quick Heal/Bitdefender/
rem Avira/Total AV/K7), personalized with this install's paths and ports.
powershell -NoProfile -NonInteractive -Command "[IO.File]::WriteAllBytes('${rootPath}\\SECURITY-SETTINGS.txt', [Convert]::FromBase64String('${guideB64}'))"

rem Windows Defender exclusions (programmatic; harmless when Defender is
rem passive behind a third-party AV). Third-party AV suites accept exclusions
rem only through their own GUI - full steps in SECURITY-SETTINGS.txt.
powershell -NoProfile -NonInteractive -Command "try { Add-MpPreference -ExclusionPath '${rootPath}','${engineBasePath}' -ExclusionProcess '${binDir}\\java.exe','${engineBasePath}\\bin\\mysqld.exe','${binDir}\\stoda.exe' -ErrorAction Stop } catch {}"

if not exist "${rootPath}\\resources\\runtime\\bin\\logs" mkdir "${rootPath}\\resources\\runtime\\bin\\logs"

powershell -NoProfile -NonInteractive -Command "[IO.File]::WriteAllBytes('${launcherPath}', [Convert]::FromBase64String('${launcherB64}'))"

set AppDir=${rootPath}\\resources\\runtime\\bin
set WrapperPath="${rootPath}\\resources\\runtime\\bin\\stoda.exe"
set SrvName="STODA"

%WrapperPath% stop %SrvName%
%WrapperPath% remove %SrvName% confirm
%WrapperPath% install %SrvName% "%ComSpec%" "/c call \\"${launcherPath}\\""
%WrapperPath% set %SrvName% DisplayName %SrvName%
%WrapperPath% set %SrvName% AppDirectory "%AppDir%"
%WrapperPath% set %SrvName% AppRestartDelay 1000
%WrapperPath% set %SrvName% Description "STODA API Server"
rem The launcher owns the JVM streams and writes them to the date-stamped
rem files. These two catch only what it cannot: cmd.exe failing before its
rem redirection exists, which would otherwise be a silent start-then-exit.
rem No date in the name, so nothing can be frozen here again.
%WrapperPath% set %SrvName% AppStdout "%AppDir%\\logs\\service_out.log"
%WrapperPath% set %SrvName% AppStderr "%AppDir%\\logs\\service_err.log"
%WrapperPath% set %SrvName% AppStdoutCreationDisposition 4
%WrapperPath% set %SrvName% AppStderrCreationDisposition 4
%WrapperPath% set %SrvName% AppRotateFiles 1
%WrapperPath% set %SrvName% AppRotateBytes 1048576
rem 1500 ms (nssm's default) is tight for a Spring Boot shutdown; give the JVM
rem 15 s to run its hooks before nssm escalates to TerminateProcess.
%WrapperPath% set %SrvName% AppStopMethodConsole 15000
%WrapperPath% set %SrvName% Start SERVICE_AUTO_START

%WrapperPath% start %SrvName%

:: Cleanup elevation task and self-delete
schtasks /Delete /TN "SHIVAM_SETUP" /F >nul 2>&1
(goto) 2>nul & del "%~f0"
`;
            await invoke('execute_elevated_script', { scriptContent: scriptBody });
        }
        this.statusText = 'Application Configured!';
        this.isSettingUp = false;
        
        this.router.navigate(['/login-stoda']);
    } catch(e) {
        this.statusText = 'Installation failed: ' + e;
    }
  }
}
